<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  activeSourceFilter: { type: String, default: 'all' },
  activeStatusFilter: { type: String, default: null },
  activeResponsibleFilter: { type: [String, Number], default: null },
  responsibles: { type: Array, default: () => [] },
  isFetchingResponsibles: { type: Boolean, default: false },
});

const emit = defineEmits(['selectSource', 'selectStatus', 'selectResponsible']);

const { t } = useI18n();

const openMenu = ref(null);

const SOURCE_STATUS_MENU_CONFIG = [
  {
    key: 'source',
    activeKey: 'activeSourceFilter',
    dropdownClass: 'min-w-48',
    options: [
      { labelKey: 'SOURCE.ALL', value: 'all', icon: 'i-lucide-files' },
      {
        labelKey: 'SOURCE.MANUAL',
        value: 'manual',
        icon: 'i-lucide-pencil-line',
      },
      {
        labelKey: 'SOURCE.DOCUMENT',
        value: 'document',
        icon: 'i-lucide-file-text',
      },
      {
        labelKey: 'SOURCE.URL',
        value: 'url',
        icon: 'i-lucide-link',
      },
    ],
  },
  {
    key: 'status',
    activeKey: 'activeStatusFilter',
    dropdownClass: 'min-w-52',
    options: [
      { labelKey: 'STATUS.ANY', value: null, icon: 'i-lucide-circle-dashed' },
      {
        labelKey: 'STATUS.APPROVED',
        value: 'approved',
        icon: 'i-lucide-check-circle',
      },
      { labelKey: 'STATUS.PENDING', value: 'pending', icon: 'i-lucide-clock' },
      {
        labelKey: 'STATUS.PAUSED',
        value: 'paused',
        icon: 'i-lucide-pause',
      },
      {
        labelKey: 'STATUS.FAILED',
        value: 'failed',
        icon: 'i-lucide-circle-x',
      },
    ],
  },
];

const filterMenus = computed(() =>
  SOURCE_STATUS_MENU_CONFIG.map(menu => {
    const active = props[menu.activeKey];
    const items = menu.options.map(opt => ({
      label: t(`CAPTAIN.RESPONSES.TRAINING_FILTERS.${opt.labelKey}`),
      value: opt.value,
      icon: opt.icon,
      action: menu.key,
      isSelected: opt.value === active,
    }));
    return {
      ...menu,
      items,
      selected: items.find(item => item.isSelected) || items[0],
    };
  })
);

const responsibleMenuItems = computed(() => [
  {
    label: t('CAPTAIN.RESPONSES.TRAINING_FILTERS.RESPONSIBLE.ALL'),
    value: null,
    action: 'responsible',
    isSelected: !props.activeResponsibleFilter,
  },
  ...props.responsibles.map(user => ({
    label: user.name,
    value: user.id,
    action: 'responsible',
    isSelected: user.id === props.activeResponsibleFilter,
  })),
]);

const selectedResponsibleLabel = computed(() => {
  const selected = responsibleMenuItems.value.find(item => item.isSelected);
  return selected
    ? selected.label
    : t('CAPTAIN.RESPONSES.TRAINING_FILTERS.RESPONSIBLE.ALL');
});

const closeMenu = () => {
  openMenu.value = null;
};

const toggleMenu = menu => {
  openMenu.value = openMenu.value === menu ? null : menu;
};

const handleMenuAction = ({ action, value }) => {
  closeMenu();
  if (action === 'source') emit('selectSource', value);
  else if (action === 'status') emit('selectStatus', value);
  else if (action === 'responsible') emit('selectResponsible', value);
};
</script>

<template>
  <div
    v-on-click-outside="closeMenu"
    class="inline-flex flex-wrap items-center gap-2 pt-2 w-fit"
  >
    <div v-for="menu in filterMenus" :key="menu.key" class="relative">
      <Button
        :icon="menu.selected.icon"
        slate
        size="sm"
        :class="{ 'bg-n-slate-9/10': openMenu === menu.key }"
        @click="toggleMenu(menu.key)"
      >
        <span class="min-w-0 truncate">{{ menu.selected.label }}</span>
        <Icon icon="i-lucide-chevron-down" class="shrink-0 size-4" />
      </Button>
      <DropdownMenu
        v-if="openMenu === menu.key"
        :menu-items="menu.items"
        :class="menu.dropdownClass"
        class="top-full mt-2 ltr:left-0 rtl:right-0"
        @action="handleMenuAction"
      />
    </div>

    <div class="relative">
      <Button
        icon="i-lucide-user"
        slate
        size="sm"
        :class="{ 'bg-n-slate-9/10': openMenu === 'responsible' }"
        @click="toggleMenu('responsible')"
      >
        <span class="min-w-0 truncate">{{ selectedResponsibleLabel }}</span>
        <Icon icon="i-lucide-chevron-down" class="shrink-0 size-4" />
      </Button>
      <DropdownMenu
        v-if="openMenu === 'responsible'"
        :menu-items="responsibleMenuItems"
        :is-loading="isFetchingResponsibles"
        class="min-w-52 top-full mt-2 ltr:left-0 rtl:right-0"
        @action="handleMenuAction"
      />
    </div>
  </div>
</template>
